import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, CreditCard, Percent, Gift, Sparkles, Plus, ArrowRight, ChevronDown, Ticket, CheckCircle2, Flame, Lock, Loader2 } from 'lucide-react';
import {
  getMaxUsablePoints,
  calculateRewardPointsDiscount,
  validateCoupon,
  calculateCouponDiscount,
  getDisplayName,
  getDisplayImage,
  formatDurationLabel,
  resolveAddonPriceForBaseValidity,
  sortDurationKeys,
  calculateExtensionPrice,
  getDurationDays,
  durationKeyFromDays,
  isCouponVisibleToUser,
  isItemEligibleForCoupon,
  resolvePriceForValidity,
} from '../../utils/helpers';
import { fetchCoupons, setCartOpen, addToCart, removeFromCart, clearCart, applyPoints, applyPromoCode, removePromoCode, updateCartItemValidity, updatePrices, selectCartItems, selectCartItemCount } from '../../store/slices/cartSlice';
import { selectAllProducts } from '../../store/slices/productsSlice';
import Button from '../common/Button';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { fetchCouponByCode } from '../../services/firebase';
import CheckoutModal from '../checkout/CheckoutModal';
import { prefetchLiveRates } from '../../services/ratesService';
import toastManager from '../../utils/toastManager';
import { useTranslation } from 'react-i18next';
import { getAuthRedirectPath } from '../../utils/authRedirect';
import { calculateDiscountedPrice } from '../../utils/priceUtils';
import { isExternalProduct, isPurchasableProduct, normalizeCategory } from '../../utils/productRules';
import { shouldApplyExtensionDiscount } from '../../utils/extensionEligibility';

const parsePriceValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[^0-9.]/g, ''));
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const resolveCartValidityOptions = (product, storeDiscounts = {}) => {
  if (!product) return [];

  const priceMap = product?.pricingRaw || product?.Price || product?.priceOptions || null;
  const options = [];
  const seen = new Set();

  if (priceMap && typeof priceMap === 'object') {
    Object.entries(priceMap).forEach(([validity, value]) => {
      const price = parsePriceValue(value);
      if (price === null) return;
      const key = String(validity || 'default').toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const discount = calculateDiscountedPrice(product, price, validity, storeDiscounts);
      options.push({
        validity,
        basePrice: price,
        finalPrice: discount.isDiscounted ? discount.finalPrice : price,
        originalPrice: discount.isDiscounted ? price : null,
        storeDiscount: discount.isDiscounted ? discount.discountPercentage : null,
      });
    });
  }

  if (options.length === 0) {
    const directPrice = parsePriceValue(product?.price);
    if (directPrice !== null) {
      const validity = product?.validity || 'monthly';
      const discount = calculateDiscountedPrice(product, directPrice, validity, storeDiscounts);
      options.push({
        validity,
        basePrice: directPrice,
        finalPrice: discount.isDiscounted ? discount.finalPrice : directPrice,
        originalPrice: discount.isDiscounted ? directPrice : product?.originalPrice || null,
        storeDiscount: discount.isDiscounted ? discount.discountPercentage : product?.storeDiscount || null,
      });
    }
  }

  const sortedKeys = sortDurationKeys(options.map((option) => option.validity));
  return options.sort((left, right) => sortedKeys.indexOf(left.validity) - sortedKeys.indexOf(right.validity));
};

const resolveExtensionValidityOptions = (product, item, extensionDiscountConfig = {}) => {
  if (!product || !item?.extensionFor || item.upgradeToValidity) return [];

  const priceMap = product?.pricingRaw || product?.Price || product?.priceOptions || null;
  const monthlyDays = getDurationDays('monthly');
  const options = [];
  const seen = new Set();

  if (priceMap && typeof priceMap === 'object') {
    Object.entries(priceMap).forEach(([validity, value]) => {
      const price = parsePriceValue(value);
      if (price === null) return;
      const durationDays = getDurationDays(validity);
      if (durationDays !== null && monthlyDays !== null && durationDays < monthlyDays) return;
      const key = String(validity || 'default').toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const finalPrice = shouldApplyExtensionDiscount(item)
        ? calculateExtensionPrice(price, validity, extensionDiscountConfig)
        : price;
      options.push({
        validity,
        basePrice: price,
        finalPrice,
        originalPrice: shouldApplyExtensionDiscount(item) && finalPrice < price ? price : null,
        storeDiscount: null,
      });
    });
  }

  const sortedKeys = sortDurationKeys(options.map((option) => option.validity));
  return options.sort((left, right) => sortedKeys.indexOf(left.validity) - sortedKeys.indexOf(right.validity));
};

const Cart = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [showOffers, setShowOffers] = useState(false);
  const [openValidityPicker, setOpenValidityPicker] = useState(null);
  const scrollLockRef = useRef(null);

  const { isOpen, appliedPoints, appliedCoupon, coupons, couponsLoading, couponError } = useSelector((state) => state.cart);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const cartItems = useSelector(selectCartItems);
  const allProducts = useSelector(selectAllProducts);
  const { settings } = useSelector((state) => state.settings);
  const storeDiscounts = useSelector((state) => state.products?.storeDiscounts || {});
  const extensionDiscountConfig = React.useMemo(
    () => settings?.extensionDiscount || {},
    [settings?.extensionDiscount]
  );

  const itemCount = useSelector(selectCartItemCount);

  

  // Build a set of cart item IDs that are now inactive in the live catalog
  const inactiveItemIds = React.useMemo(() => {
    const ids = new Set();
    cartItems.forEach(item => {
      // Extensions don't apply — skip them
      if (item.category === 'extension') return;
      const baseId = item.addonFor || item.baseProductId || item.id?.replace('-extension', '');
      const liveProduct = allProducts.find(p => p.id === item.id || p.id === baseId);
      if (liveProduct && liveProduct.status && liveProduct.status !== 'Active' && liveProduct.status !== 'active') {
        ids.add(item.id);
      }
    });
    return ids;
  }, [cartItems, allProducts]);

  const getItemTotal = (item) => {
    const price = typeof item.price === 'string' ?
      parseFloat(item.price.replace(/[^0-9.]/g, '')) : item.price;
    return price;
  };

  const getExtensionCombinedValidity = (extensionItem, extensionValidity) => {
    const extensionDays = getDurationDays(extensionValidity);
    if (extensionDays === null) return extensionValidity;

    const remainingDays = Number(extensionItem?.remainingDays);
    if (Number.isFinite(remainingDays) && remainingDays > 0) {
      return durationKeyFromDays(remainingDays + extensionDays);
    }

    return extensionValidity;
  };

  // Calculate totals
  const subtotal = cartItems.reduce((total, item) => total + getItemTotal(item), 0);
  const pointsRate = settings?.pointsRate || 20;
  const pointsDiscount = calculateRewardPointsDiscount(appliedPoints, pointsRate);
  const promoDiscount = calculateCouponDiscount(appliedCoupon, cartItems);
  const finalTotal = Math.max(0, subtotal - pointsDiscount - promoDiscount);

  // Auto-apply promo code from URL param ?coupon=CODE or ?promo=CODE
  useEffect(() => {
    if (!location?.search || !cartItems.length) return;
    const params = new URLSearchParams(location.search);
    const urlPromo = params.get("coupon") || params.get("promo");
    if (!urlPromo || appliedCoupon) return;

    const cleanCode = urlPromo.trim().toUpperCase();
    (async () => {
      let pool = coupons;
      let found = pool.find(c => (c.code || "").toUpperCase() === cleanCode);
      if (!found) {
        found = await fetchCouponByCode(cleanCode, user?.uid || user?.id);
        if (found) pool = [...coupons, found];
      }
      if (found) {
        const val = validateCoupon(cleanCode, pool, { items: cartItems, total: subtotal }, user?.usedCoupons, user);
        if (val && !val.error) {
          dispatch(applyPromoCode({ coupon: val.coupon }));
          toastManager.success(`Coupon ${cleanCode} applied from link!`);
        }
      }
    })();
  }, [location?.search, cartItems, coupons, appliedCoupon, subtotal, user, dispatch]);
  const cartProductIds = React.useMemo(() => new Set(cartItems.flatMap((item) => [
    String(item.id).replace('-extension', ''),
    item.addonFor && item.addonId ? `${item.addonFor}::addon::${item.addonId}` : null
  ].filter(Boolean))), [cartItems]);
  const cartCategories = React.useMemo(
    () => new Set(cartItems.map((item) => normalizeCategory(item.category)).filter(Boolean)),
    [cartItems]
  );

  const suggestedProducts = React.useMemo(() => {
    if (!Array.isArray(allProducts) || allProducts.length === 0) return [];

    return allProducts
      .filter((product) => {
        if (!product?.id || cartProductIds.has(String(product.id))) return false;
        if (!isPurchasableProduct(product) || isExternalProduct(product)) return false;
        const status = String(product.status || product.Status || 'Active').toLowerCase();
        if (status !== 'active') return false;
        return resolveCartValidityOptions(product, storeDiscounts).length > 0;
      })
      .map((product) => {
        const validityOptions = resolveCartValidityOptions(product, storeDiscounts);
        const bestOption = validityOptions.reduce(
          (best, option) => (option.finalPrice < best.finalPrice ? option : best),
          validityOptions[0]
        );
        return {
          product,
          validity: bestOption?.validity || null,
          basePrice: bestOption?.basePrice || null,
          finalPrice: bestOption?.finalPrice || null,
          originalPrice: bestOption?.originalPrice || null,
          storeDiscount: bestOption?.storeDiscount || null,
          validityOptions,
          categoryMatch: cartCategories.has(normalizeCategory(product.category)),
          popularity: Number(product.popularity || 0),
        };
      })
      .sort((a, b) => {
        if (a.categoryMatch !== b.categoryMatch) return a.categoryMatch ? -1 : 1;
        if (Boolean(a.product.featured) !== Boolean(b.product.featured)) return a.product.featured ? -1 : 1;
        return b.popularity - a.popularity;
      })
      .slice(0, 5);
  }, [allProducts, cartCategories, cartProductIds, storeDiscounts]);

  const checkCouponEligibility = React.useCallback((coupon) => {
    if (!coupon) return { isEligible: false, reason: "Invalid coupon", shortfall: 0 };
    if (!cartItems || cartItems.length === 0) {
      const minPurchase = Number(coupon.minPurchaseAmount || 0);
      return {
        isEligible: false,
        reason: minPurchase > 0 ? t("coupon.min_purchase", { amount: "$" + minPurchase.toFixed(2) }) : t("cart.available_offers"),
        shortfall: minPurchase
      };
    }

    // Global coupon usage limit check
    const usageLimit = Number(coupon.usageLimit ?? 0) || 0;
    const timesUsed = Number(coupon.timesUsed ?? 0) || 0;
    if (usageLimit > 0 && timesUsed >= usageLimit) {
      return { isEligible: false, reason: "Usage limit reached", shortfall: 0 };
    }

    // Per-user usage limit check
    if (user?.usedCoupons) {
      const normalizedCode = String(coupon.code || "").trim().toUpperCase();
      const usageRecord = user.usedCoupons[normalizedCode];
      if (usageRecord) {
        let userCount = 0;
        if (usageRecord.usedAt || usageRecord.orderId) userCount += 1;
        const reservedKeys = new Set(["orderId", "usedAt", "discount", "usedBy", "status", "approvedAt", "releasedAt", "releaseReason"]);
        const nestedUsages = Object.keys(usageRecord).filter((k) =>
          !reservedKeys.has(k) && typeof usageRecord[k] === "object" && usageRecord[k] !== null
        );
        userCount += nestedUsages.length;
        const maxPerUser = Number(coupon.maxUsagePerUser ?? 1);
        if (userCount >= maxPerUser) {
          return { isEligible: false, reason: "Usage limit reached for account", shortfall: 0 };
        }
      }
    }

    // First order only restriction check
    if (coupon.firstOrderOnly) {
      const priorOrders = Number(
        user?.["Total order"] ??
        user?.totalOrders ??
        user?.total_orders ??
        (Array.isArray(user?.orders) ? user.orders.length : 0)
      ) || 0;
      if (priorOrders > 0) {
        return { isEligible: false, reason: "Valid for 1st order only", shortfall: 0 };
      }
    }

    // Cart items eligibility check (products, categories, plan validity)
    const eligibleItems = cartItems.filter((item) => isItemEligibleForCoupon(item, coupon));
    if (eligibleItems.length === 0) {
      return { isEligible: false, reason: "Not applicable to items in cart", shortfall: 0 };
    }

    // Minimum purchase requirement on eligible items
    const eligibleTotal = eligibleItems.reduce((sum, item) => sum + getItemTotal(item), 0);
    const minPurchase = Number(coupon.minPurchaseAmount || 0);
    if (minPurchase > 0 && eligibleTotal < minPurchase) {
      const shortfall = minPurchase - eligibleTotal;
      return {
        isEligible: false,
        reason: t("coupon.add_to_unlock", { amount: "$" + shortfall.toFixed(2) }, "Add $" + shortfall.toFixed(2) + " more to unlock"),
        shortfall
      };
    }

    return { isEligible: true, reason: null, shortfall: 0 };
  }, [cartItems, user, t]);

  const activeCouponsList = React.useMemo(() => {
    if (!coupons) return [];

    return coupons
      .filter((c) => {
        // 1. Must be active and within start/end dates
        if (c.status && c.status.toLowerCase() !== "active") return false;
        if (c.isArchived || c.storage === "archived") return false;
        if (c.startDate && new Date(c.startDate) > new Date()) return false;
        if (c.endDate && new Date(c.endDate) < new Date()) return false;

        // 2. Hide already applied coupon from suggestions
        if (c.code && appliedCoupon?.code && c.code.toUpperCase() === appliedCoupon.code.toUpperCase()) {
          return false;
        }

        // 3. User visibility check:
        // - ONLY private coupon hidden
        // - User-specific coupons hidden for all except allowed users
        // - Public coupons visible to all
        return isCouponVisibleToUser(c, user);
      })
      .sort((a, b) => {
        const aEligible = checkCouponEligibility(a).isEligible;
        const bEligible = checkCouponEligibility(b).isEligible;
        if (aEligible && !bEligible) return -1;
        if (!aEligible && bEligible) return 1;
        const aVal = Number(a.value) || 0;
        const bVal = Number(b.value) || 0;
        return bVal - aVal;
      });
  }, [coupons, appliedCoupon, user, checkCouponEligibility]);

  const betterUpsellCoupon = React.useMemo(() => {
    if (!coupons || cartItems.length === 0) return null;

    const lockedCoupons = coupons.filter((c) => {
      if (c.status && c.status.toLowerCase() !== "active") return false;
      if (c.isArchived || c.storage === "archived") return false;
      if (c.startDate && new Date(c.startDate) > new Date()) return false;
      if (c.endDate && new Date(c.endDate) < new Date()) return false;
      if (c.code && appliedCoupon?.code && c.code.toUpperCase() === appliedCoupon.code.toUpperCase()) {
        return false;
      }
      if (!isCouponVisibleToUser(c, user)) return false;

      const eligibility = checkCouponEligibility(c);
      return !eligibility.isEligible && eligibility.shortfall > 0;
    });

    let bestCoupon = null;
    let maxExpectedDiscount = promoDiscount;

    for (const c of lockedCoupons) {
      const minPurchase = Number(c.minPurchaseAmount || 0);
      let expectedDiscount = 0;
      if (c.type === "percentage") {
        expectedDiscount = minPurchase * (Number(c.value) / 100);
        if (c.maxDiscount && c.maxDiscount > 0) {
          expectedDiscount = Math.min(expectedDiscount, Number(c.maxDiscount));
        }
      } else {
        expectedDiscount = Number(c.value);
      }

      if (expectedDiscount > maxExpectedDiscount) {
        maxExpectedDiscount = expectedDiscount;
        bestCoupon = c;
      }
    }
    return bestCoupon;
  }, [coupons, cartItems, promoDiscount, appliedCoupon, user, checkCouponEligibility]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCoupons());
      prefetchLiveRates();
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen || checkoutModalOpen) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const previousStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width
    };

    scrollLockRef.current = { scrollY, previousStyles };
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      const lock = scrollLockRef.current;
      scrollLockRef.current = null;

      document.body.classList.remove('modal-open');
      document.body.style.overflow = lock?.previousStyles.overflow || '';
      document.body.style.position = lock?.previousStyles.position || '';
      document.body.style.top = lock?.previousStyles.top || '';
      document.body.style.width = lock?.previousStyles.width || '';

      window.scrollTo({
        top: lock?.scrollY || 0,
        left: 0,
        behavior: 'auto'
      });
    };
  }, [checkoutModalOpen, isOpen]);

  // Auto-adjust points when cart total or coupon changes
  useEffect(() => {
    // Only adjust if points are currently enabled
    if (appliedPoints > 0) {
      const userPoints = user?.rewardPoints || 0;

      // Calculate what the max points SHOULD be right now
      const currentPromoDiscount = calculateCouponDiscount(appliedCoupon, cartItems);
      const adjustedSubtotal = Math.max(0, subtotal - currentPromoDiscount);
      const maxUsable = getMaxUsablePoints(userPoints, adjustedSubtotal, pointsRate);

      // If the current applied amount differs from the allowed max, update it
      // This handles both scaling DOWN (if price drops) and SCALING UP (if price increases)
      // provided the user has enough points.
      if (appliedPoints !== maxUsable) {
        dispatch(applyPoints(maxUsable));

        // Optional: Notify user if the change is significant or zeroing out
        if (maxUsable === 0) {
          // toastManager.info("Points removed as cart total is 0");
        } else if (Math.abs(appliedPoints - maxUsable) > 10) {
          const ptsDiscount = calculateRewardPointsDiscount(maxUsable, pointsRate);
          toastManager.info(t('cart.points_updated', { pts: maxUsable, discount: ptsDiscount.toFixed(2) }));
        }
      }
    }
  }, [subtotal, appliedCoupon, user, appliedPoints, dispatch, cartItems, pointsRate, t]);



  const handleTogglePoints = () => {
    if (!isAuthenticated) {
      toastManager.error(t('cart.signin_points_error'));
      dispatch(setCartOpen(false));
      navigate(getAuthRedirectPath());
      return;
    }

    // If points are already applied, toggle OFF (remove them)
    if (appliedPoints > 0) {
      dispatch(applyPoints(0));
      return;
    }

    // Toggle ON: Calculate and apply max usable points
    const userPoints = user.rewardPoints || 0;

    // Calculate current coupon discount to ensure points don't exceed remaining total
    const currentPromoDiscount = calculateCouponDiscount(appliedCoupon, cartItems);
    const adjustedSubtotal = Math.max(0, subtotal - currentPromoDiscount);

    const pointsRate = settings?.pointsRate || 20;
    const maxUsable = getMaxUsablePoints(userPoints, adjustedSubtotal, pointsRate);

    if (maxUsable <= 0) {
      toastManager.error(t('cart.points_error'));
      return;
    }

    // Apply the calculates max points
    dispatch(applyPoints(maxUsable));

    // Calculate immediate discount for toast feedback
    const discount = calculateRewardPointsDiscount(maxUsable, pointsRate);
    toastManager.success(t('cart.points_redeemed', { pts: maxUsable, discount: discount.toFixed(2) }));
  };

  const handleApplyPromo = () => {
    if (!isAuthenticated) {
      toastManager.error(t('cart.signin_coupon_error'));
      dispatch(setCartOpen(false));
      navigate(getAuthRedirectPath());
      return;
    }

    if (!promoInput.trim()) return;
    const validation = validateCoupon(promoInput, coupons, { items: cartItems, total: subtotal }, user?.usedCoupons, user);
    if (!validation || validation.error) {
      toastManager.error(validation?.error || 'Unable to apply coupon');
      return;
    }

    dispatch(applyPromoCode({ coupon: validation.coupon }));
    setPromoInput('');
    setShowOffers(false);
    const discountAmount = calculateCouponDiscount(validation.coupon, cartItems);
    toastManager.success(t('cart.coupon_applied', { discount: discountAmount.toFixed(2) }));
  };

  const handleDirectApply = (coupon) => {
    if (!isAuthenticated) {
      toastManager.error(t('cart.signin_coupon_error'));
      return;
    }
    const validation = validateCoupon(coupon.code || coupon.id, coupons, { items: cartItems, total: subtotal }, user?.usedCoupons, user);
    if (!validation || validation.error) {
      toastManager.error(validation?.error || 'Unable to apply coupon');
      return;
    }
    dispatch(applyPromoCode({ coupon: validation.coupon }));
    setShowOffers(false);
    const discountAmount = calculateCouponDiscount(validation.coupon, cartItems);
    toastManager.success(t('cart.coupon_applied', { discount: discountAmount.toFixed(2) }));
  };

  const buildCartProductForValidity = (product, option) => {
    if (!product || !option || option.finalPrice === null) return null;

    const displayName = getDisplayName(product);
    return {
      id: product.id,
      name: displayName,
      price: option.finalPrice,
      image: getDisplayImage(product),
      category: product.category,
      addonFor: product.addonFor || product.baseProductId || product.parentProductId || null,
      addonId: product.addonId || null,
      addonName: product.addonName || product.name || null,
      baseProductName: product.baseProductName || product.parentProductName || null,
      originalPrice: option.originalPrice,
      storeDiscount: option.storeDiscount,
    };
  };

  const buildRelatedAddonUpdates = (baseProduct, nextValidity) => {
    if (!baseProduct?.id || !nextValidity) return [];
    const addons = Array.isArray(baseProduct.addons) ? baseProduct.addons : [];
    return cartItems
      .filter((item) => item.addonFor === baseProduct.id && item.addonId)
      .map((item) => {
        const addon = addons.find((candidate) => String(candidate.id) === String(item.addonId));
        if (!addon) return null;
        const adjusted = resolveAddonPriceForBaseValidity(
          addon.pricingRaw || addon.Price || {},
          baseProduct.pricingRaw || baseProduct.Price || {},
          nextValidity
        );
        if (adjusted.price === null || adjusted.price < 0) return null;
        return {
          id: item.id,
          fromValidity: item.validity || null,
          item: {
            ...item,
            price: adjusted.price,
            validity: nextValidity,
            addonAdjustedDays: adjusted.days || null,
            addonBaseValidity: nextValidity,
            addonPricingSource: 'cart_main'
          }
        };
      })
      .filter(Boolean);
  };

  const buildExtensionAddonUpdates = (baseProduct, extensionItem, extensionValidity) => {
    if (!baseProduct?.id || !extensionItem) return [];
    const combinedValidity = getExtensionCombinedValidity(extensionItem, extensionValidity);
    return buildRelatedAddonUpdates(baseProduct, combinedValidity).map((related) => ({
      ...related,
      item: {
        ...related.item,
        addonBaseValidity: combinedValidity,
        addonPricingSource: extensionItem.renewal ? 'renewal_combined' : 'extension_combined'
      }
    }));
  };

  useEffect(() => {
    if (!cartItems.some((item) => item.extensionFor) || !Array.isArray(allProducts) || allProducts.length === 0) {
      return;
    }

    let changed = false;
    const syncedItems = cartItems.map((item) => {
      if (!item.extensionFor || item.upgradeToValidity) return item;
      const productId = String(item.id || '').replace(/-extension$/, '');
      const liveProduct = allProducts.find((product) => String(product.id) === productId);
      const basePrice = resolvePriceForValidity(liveProduct?.pricingRaw || liveProduct?.Price || {}, item.validity);
      if (basePrice === null) return item;

      const nextPrice = shouldApplyExtensionDiscount(item)
        ? calculateExtensionPrice(basePrice, item.validity, extensionDiscountConfig)
        : Number(basePrice);
      if (Math.abs(Number(item.price || 0) - nextPrice) <= 0.01) return item;

      changed = true;
      return {
        ...item,
        price: nextPrice,
        originalPrice: shouldApplyExtensionDiscount(item) && nextPrice < Number(basePrice) ? Number(basePrice) : null,
        updatedAt: Date.now()
      };
    });

    if (changed) {
      dispatch(updatePrices(syncedItems));
    }
  }, [allProducts, cartItems, dispatch, extensionDiscountConfig]);

  useEffect(() => {
    if (!cartItems.some((item) => item.addonFor && item.addonId) || !Array.isArray(allProducts) || allProducts.length === 0) {
      return;
    }

    let changed = false;
    const syncedItems = cartItems.map((item) => {
      if (!item.addonFor || !item.addonId) return item;
      const baseProduct = allProducts.find((product) => String(product.id) === String(item.addonFor));
      const addon = Array.isArray(baseProduct?.addons)
        ? baseProduct.addons.find((candidate) => String(candidate.id) === String(item.addonId))
        : null;
      if (!baseProduct || !addon) return item;

      const extensionItem = cartItems.find((cartItem) => (
        cartItem.extensionFor &&
        String(cartItem.id || '').replace(/-extension$/, '') === String(item.addonFor)
      )) || null;
      const effectiveValidity = extensionItem
        ? getExtensionCombinedValidity(extensionItem, extensionItem.validity)
        : item.validity;

      const adjusted = resolveAddonPriceForBaseValidity(
        addon.pricingRaw || addon.Price || {},
        baseProduct.pricingRaw || baseProduct.Price || {},
        effectiveValidity
      );
      if (adjusted.price === null || adjusted.price < 0 || Math.abs(Number(item.price || 0) - adjusted.price) <= 0.01) {
        return item;
      }

      changed = true;
      return {
        ...item,
        price: adjusted.price,
        addonAdjustedDays: adjusted.days || item.addonAdjustedDays || null,
        addonBaseValidity: effectiveValidity || item.addonBaseValidity || null,
        addonPricingSource: extensionItem ? 'extension_combined' : item.addonPricingSource || 'cart_sync',
        updatedAt: Date.now()
      };
    });

    if (changed) {
      dispatch(updatePrices(syncedItems));
    }
  }, [allProducts, cartItems, dispatch]);

  const getBaseCartItem = (productId) => cartItems.find((item) => (
    item.id === productId && !item.addonFor && !item.addonId && !item.extensionFor
  )) || null;

  const handleAddSuggestedProduct = (suggestion, selectedOption = null) => {
    if (!suggestion?.product) return;

    const options = suggestion.validityOptions?.length
      ? suggestion.validityOptions
      : resolveCartValidityOptions(suggestion.product, storeDiscounts);

    if (!selectedOption && options.length > 1) {
      setOpenValidityPicker((current) => (
        current?.type === 'suggestion' && current?.id === suggestion.product.id ? null : { type: 'suggestion', id: suggestion.product.id }
      ));
      return;
    }

    const option = selectedOption || options[0];
    const productForCart = buildCartProductForValidity(suggestion.product, option);
    if (!productForCart) return;

    dispatch(addToCart({
      product: productForCart,
      validity: option.validity,
      relatedItems: buildRelatedAddonUpdates(suggestion.product, option.validity)
    }));
    setOpenValidityPicker(null);
    const existingBaseItem = getBaseCartItem(suggestion.product.id);
    toastManager.success(
      existingBaseItem && (existingBaseItem.validity || null) !== (option.validity || null)
        ? t('cart.validity_updated', 'Plan updated')
        : t('cart.added_to_cart', { name: getDisplayName(suggestion.product) })
    );
  };

  const handleChangeCartItemValidity = (item, option) => {
    const liveProduct = allProducts.find(p => p.id === item.id || p.id === item.addonFor || p.id === String(item.id).replace('-extension', ''));
    if (!liveProduct || !option) return;

    const isExtensionItem = Boolean(item.extensionFor);
    const productForCart = isExtensionItem
      ? {
          id: item.id,
          name: item.name,
          price: option.finalPrice,
          image: item.image || getDisplayImage(liveProduct),
          category: 'extension',
          originalPrice: option.originalPrice || null,
          storeDiscount: null,
        }
      : buildCartProductForValidity(liveProduct, option);
    if (!productForCart) return;

    dispatch(updateCartItemValidity({
      id: item.id,
      fromValidity: item.validity || null,
      item: {
        ...productForCart,
        id: item.id,
        name: isExtensionItem ? item.name : productForCart.name,
        category: isExtensionItem ? 'extension' : productForCart.category,
        extensionFor: item.extensionFor || null,
        renewal: item.renewal || false,
        extensionDiscountEligible: Boolean(item.extensionDiscountEligible),
        accessSource: item.accessSource || null,
        upgradeFromValidity: null,
        upgradeToValidity: null,
        upgradeDays: null,
        remainingDays: item.remainingDays || null,
        validity: option.validity,
      },
      relatedItems: isExtensionItem
        ? buildExtensionAddonUpdates(liveProduct, item, option.validity)
        : buildRelatedAddonUpdates(liveProduct, option.validity)
    }));
    setOpenValidityPicker(null);
    toastManager.success(t('cart.validity_updated', 'Plan updated'));
  };

  const formatPrice = (price) => {
    let result = '$0.00';
    if (typeof price === 'string') {
      const numericPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
      result = isNaN(numericPrice) ? price : `$${numericPrice.toFixed(2)}`;
    } else if (typeof price === 'number') {
      result = `$${price.toFixed(2)}`;
    }
    return <span className="notranslate" translate="no">{result}</span>;
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      toastManager.error(t('cart.signin_checkout_error'));
      dispatch(setCartOpen(false));
      navigate(getAuthRedirectPath());
      return;
    }

    // Block checkout if any item became inactive
    if (inactiveItemIds.size > 0) {
      toastManager.error(t('cart.inactive_items_error', 'Some items in your cart are no longer available. Please remove them before checkout.'));
      return;
    }

    // Immediately prefetch live rates as soon as user clicks checkout in cart
    prefetchLiveRates();
    setCheckoutModalOpen(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <React.Fragment key="cart-container">
          {/* Backdrop */}
          <motion.div
            key="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-950/65 backdrop-blur-sm z-[100]"
            onClick={() => dispatch(setCartOpen(false))}
          />

          {/* Cart Sidebar */}
          <motion.div
            key="cart-sidebar"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full sm:max-w-md lg:max-w-lg xl:max-w-xl bg-white dark:bg-[#0b1220] shadow-2xl z-[101] flex flex-col overscroll-contain"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-3.5 py-3 sm:px-4 sm:py-3.5 border-b border-gray-200/80 dark:border-white/10 bg-white/95 dark:bg-[#0b1220]/95 backdrop-blur-sm">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-500 text-white shadow-md shadow-primary-500/25">
                    <ShoppingBag className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-sm sm:text-base font-bold text-gray-950 dark:text-white leading-tight">
                      {t('cart.title')}
                    </h2>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        {itemCount > 0 ? `${itemCount} ${itemCount === 1 ? 'item' : 'items'}` : t('cart.empty_title')}
                      </span>
                      {itemCount > 0 && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.2 text-[10px] font-bold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300">
                          <Sparkles className="h-2.5 w-2.5" />
                          {t('cart.smart_picks', 'Smart picks ready')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {itemCount > 0 && (
                  <button
                    onClick={() => dispatch(clearCart())}
                    className="px-2 py-1 text-xs font-semibold text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20"
                    title={t('cart.clear_all')}
                  >
                    {t('common.clear', 'Clear')}
                  </button>
                )}
                <button
                  onClick={() => dispatch(setCartOpen(false))}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-white/10 rounded-xl transition-colors duration-200"
                  aria-label="Close cart"
                >
                  <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {cartItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <ShoppingBag className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('cart.empty_title')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    {t('cart.empty_subtitle')}
                  </p>
                  <Button
                    as={Link}
                    to="/products"
                    onClick={() => dispatch(setCartOpen(false))}
                    variant="primary"
                  >
                    {t('cart.start_shopping')}
                  </Button>
                </div>
              ) : (
                <div className="px-3.5 py-3 sm:px-4 sm:py-3.5 space-y-3">
                  {/* Cart Items */}
                  <div className="space-y-2">
                    <AnimatePresence>
                      {cartItems.map((item) => {
                        const isItemInactive = inactiveItemIds.has(item.id);
                        const liveProduct = allProducts.find(p => p.id === item.id || p.id === String(item.id).replace('-extension', ''));
                        const itemValidityOptions = item.addonFor
                          ? []
                          : item.extensionFor
                            ? resolveExtensionValidityOptions(liveProduct, item, extensionDiscountConfig)
                            : liveProduct ? resolveCartValidityOptions(liveProduct, storeDiscounts) : [];
                        const itemPickerOpen = openValidityPicker?.type === 'cart-item'
                          && openValidityPicker?.id === item.id
                          && (openValidityPicker?.validity || null) === (item.validity || null);
                        return (
                          <motion.div
                            key={`${item.id}-${item.validity}`}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className={`rounded-xl border p-2 sm:p-2.5 transition-all ${isItemInactive
                              ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800/70'
                              : 'bg-white dark:bg-white/[0.035] border-gray-200/80 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 shadow-sm'
                            }`}
                          >
                            <div className="relative flex items-center gap-2.5 pr-8">
                              <div className="flex-shrink-0">
                                <img
                                  src={item.image || '/icon.png'}
                                  alt={item.name}
                                  className={`w-11 h-11 sm:w-12 sm:h-12 rounded-lg object-cover bg-gray-100 dark:bg-gray-800 border border-gray-100 dark:border-white/5 ${isItemInactive ? 'grayscale opacity-60' : ''}`}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-gray-950 dark:text-white text-xs sm:text-sm leading-tight line-clamp-1 notranslate" translate="no">
                                  {item.name}
                                </h4>
                                {item.addonFor && (
                                  <p className="mt-0.5 line-clamp-1 text-[10px] font-semibold text-violet-600 dark:text-violet-300">
                                    {t('cart.addon_for', 'Addon for')} <span className="notranslate" translate="no">{item.baseProductName || item.addonFor}</span>
                                  </p>
                                )}

                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  {item.validity && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (itemValidityOptions.length <= 1) return;
                                        setOpenValidityPicker(itemPickerOpen ? null : { type: 'cart-item', id: item.id, validity: item.validity || null });
                                      }}
                                      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold capitalize ring-1 transition-colors ${itemValidityOptions.length > 1
                                        ? 'bg-primary-50 text-primary-700 ring-primary-200 hover:bg-primary-100 dark:bg-primary-400/10 dark:text-primary-200 dark:ring-primary-400/20'
                                        : 'bg-gray-50 text-gray-600 ring-gray-200 dark:bg-white/5 dark:text-gray-300 dark:ring-white/10'
                                      }`}
                                    >
                                      <span>{formatDurationLabel(item.validity)} {t('cart.plan')}</span>
                                      {itemValidityOptions.length > 1 && <span className="text-[9px] opacity-70">▾</span>}
                                    </button>
                                  )}
                                  {itemValidityOptions.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => setOpenValidityPicker(itemPickerOpen ? null : { type: 'cart-item', id: item.id, validity: item.validity || null })}
                                      className="text-[10px] font-bold text-primary-600 hover:text-primary-700 dark:text-primary-300"
                                    >
                                      {t('cart.change_validity', 'Change')}
                                    </button>
                                  )}
                                </div>

                                {isItemInactive && (
                                  <p className="mt-1 flex items-center gap-1 text-[10px] font-bold text-red-600 dark:text-red-400">
                                    {t('cart.item_unavailable', 'No longer available - remove to proceed')}
                                  </p>
                                )}

                                <div className="mt-1 flex flex-wrap items-baseline gap-1.5">
                                  <div className="flex items-baseline gap-1">
                                    {item.originalPrice && (
                                      <span className="text-[10px] text-gray-400 line-through mr-0.5">
                                        {formatPrice(item.originalPrice)}
                                      </span>
                                    )}
                                    <p className="text-xs sm:text-sm font-black text-primary-600 dark:text-primary-300">
                                      {formatPrice(item.price)}
                                    </p>
                                    {item.validity && (
                                      <span className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">
                                        /{formatDurationLabel(item.validity)}
                                      </span>
                                    )}
                                  </div>
                                  {item.storeDiscount && (
                                    <span className="text-[9px] font-bold text-red-600 bg-red-50 dark:bg-red-500/10 dark:text-red-300 px-1.5 py-0.2 rounded-full">
                                      -{item.storeDiscount}% {t('product.off')}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <button
                                onClick={() => {
                                  dispatch(removeFromCart({ id: item.id, validity: item.validity }));
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors duration-200"
                                title="Remove item"
                                aria-label={`Remove ${item.name}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {itemPickerOpen && itemValidityOptions.length > 1 && (
                              <div className="mt-2 grid grid-cols-2 gap-1 sm:grid-cols-3 rounded-lg bg-gray-50/80 dark:bg-white/[0.03] p-1.5 border border-gray-200/60 dark:border-white/5">
                                {itemValidityOptions.map((option) => {
                                  const isSelected = (option.validity || null) === (item.validity || null);
                                  return (
                                    <button
                                      key={option.validity || 'default'}
                                      type="button"
                                      onClick={() => !isSelected && handleChangeCartItemValidity(item, option)}
                                      className={`rounded-md border px-2 py-1.5 text-left transition-colors ${isSelected
                                        ? 'border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-400/40 dark:bg-primary-400/10 dark:text-primary-100'
                                        : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-primary-50/60 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]'
                                      }`}
                                    >
                                      <span className="block text-[9px] font-bold capitalize text-gray-500 dark:text-gray-400">
                                        {option.validity ? formatDurationLabel(option.validity) : t('common.default', 'Default')}
                                      </span>
                                      <span className="mt-0.5 block text-[11px] font-black text-gray-950 dark:text-white">
                                        {formatPrice(option.finalPrice)}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>

                  {suggestedProducts.length > 0 && (
                    <section className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-2.5 shadow-sm dark:border-white/10 dark:bg-white/[0.025]">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300">
                            <Sparkles className="h-3 w-3" />
                          </span>
                          <h3 className="text-xs font-bold text-gray-900 dark:text-white truncate">
                            {t('cart.complete_cart', 'Complete your cart')}
                          </h3>
                        </div>
                        <Link
                          to="/products"
                          onClick={() => dispatch(setCartOpen(false))}
                          className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-bold text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-300"
                        >
                          {t('common.view_all')}
                          <ArrowRight className="h-2.5 w-2.5" />
                        </Link>
                      </div>

                      <div className="-mx-2.5 flex gap-2 overflow-x-auto px-2.5 pb-0.5 no-scrollbar overscroll-x-contain">
                        {suggestedProducts.map((suggestion) => {
                          const productName = getDisplayName(suggestion.product);
                          const suggestionPickerOpen = openValidityPicker?.type === 'suggestion' && openValidityPicker?.id === suggestion.product.id;
                          return (
                            <div
                              key={suggestion.product.id}
                              className="w-[124px] sm:w-[130px] shrink-0 overflow-hidden rounded-lg border border-gray-200/80 bg-white dark:border-white/10 dark:bg-gray-900/90 shadow-sm flex flex-col justify-between"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  dispatch(setCartOpen(false));
                                  navigate(`/product/${suggestion.product.id}`);
                                }}
                                className="block w-full text-left"
                              >
                                <div className="relative h-16 bg-gray-100 dark:bg-gray-800">
                                  <img
                                    src={getDisplayImage(suggestion.product)}
                                    alt={productName}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                  />
                                  {suggestion.storeDiscount && (
                                    <span className="absolute left-1.5 top-1.5 rounded-full bg-red-500 px-1 py-0.2 text-[9px] font-black text-white">
                                      -{suggestion.storeDiscount}%
                                    </span>
                                  )}
                                </div>
                              </button>
                              <div className="p-1.5 flex flex-col justify-between flex-1">
                                <p className="text-[11px] font-bold leading-tight text-gray-950 line-clamp-1 dark:text-white notranslate" translate="no" title={productName}>
                                  {productName}
                                </p>
                                <div className="mt-1 flex items-center justify-between gap-1">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-black text-primary-600 dark:text-primary-300">
                                      {formatPrice(suggestion.finalPrice)}
                                    </p>
                                    {suggestion.validity && (
                                      <p className="truncate text-[9px] capitalize text-gray-400">
                                        {formatDurationLabel(suggestion.validity)}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleAddSuggestedProduct(suggestion)}
                                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white transition-transform hover:bg-primary-700 active:scale-95 shadow-sm"
                                    aria-label={`Add ${productName}`}
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                {suggestionPickerOpen && suggestion.validityOptions?.length > 1 && (
                                  <div className="mt-1.5 grid gap-1">
                                    {suggestion.validityOptions.map((option) => (
                                      <button
                                        key={option.validity || 'default'}
                                        type="button"
                                        onClick={() => handleAddSuggestedProduct(suggestion, option)}
                                        className="flex items-center justify-between gap-1 rounded border border-primary-200 bg-white px-1.5 py-1 text-left hover:bg-primary-50 dark:border-primary-400/20 dark:bg-white/[0.04]"
                                      >
                                        <span className="truncate text-[9px] font-bold capitalize text-gray-700 dark:text-gray-200">
                                          {option.validity ? formatDurationLabel(option.validity) : t('common.default', 'Default')}
                                        </span>
                                        <span className="shrink-0 text-[9px] font-black text-primary-600 dark:text-primary-300">
                                          {formatPrice(option.finalPrice)}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  {/* Reward Points Section */}
                  {user && (
                    <div className={`p-2.5 sm:p-3 rounded-xl border transition-all ${
                      appliedPoints > 0
                        ? 'bg-violet-50/80 border-violet-200 dark:bg-violet-500/10 dark:border-violet-500/25'
                        : 'bg-white dark:bg-white/[0.035] border-gray-200/80 dark:border-white/10'
                    }`}>
                      <div className="flex items-center justify-between gap-2.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            appliedPoints > 0 ? 'bg-violet-100 dark:bg-violet-500/20' : 'bg-gray-100 dark:bg-white/10'
                          }`}>
                            <Gift className={`w-4 h-4 ${appliedPoints > 0 ? 'text-violet-600 dark:text-violet-400' : 'text-gray-500'}`} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-gray-900 dark:text-white leading-tight">{t('cart.use_points')}</p>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                              {t('cart.available_points')}: <span className="font-semibold text-gray-700 dark:text-gray-300">{user.rewardPoints || 0}</span> (~${Math.floor((user.rewardPoints || 0) / (settings?.pointsRate || 20))})
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={handleTogglePoints}
                          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            appliedPoints > 0 ? 'bg-violet-600' : 'bg-gray-200 dark:bg-gray-700'
                          }`}
                          role="switch"
                          aria-checked={appliedPoints > 0}
                          title="Toggle Points Redemption"
                        >
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              appliedPoints > 0 ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Active Stats */}
                      <AnimatePresence>
                        {appliedPoints > 0 && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-2 pt-2 border-t border-dashed border-violet-200 dark:border-violet-500/30 flex items-center justify-between text-xs"
                          >
                            <span className="text-gray-600 dark:text-gray-400 text-[11px]">{t('cart.redeeming')}: <span className="font-bold text-violet-600 dark:text-violet-400">{appliedPoints} pts</span></span>
                            <span className="font-black text-green-600 dark:text-green-400 notranslate" translate="no">-${pointsDiscount.toFixed(2)}</span>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Promo Code Section */}
                  <div className="relative z-10">
                    <div className="relative">
                      {appliedCoupon ? (
                        <div className="relative overflow-hidden rounded-2xl border border-emerald-400/80 bg-gradient-to-br from-emerald-50 via-white to-teal-50/60 p-3 shadow-xs dark:border-emerald-500/40 dark:from-emerald-950/40 dark:via-gray-900 dark:to-teal-950/30">
                          <div className="flex items-center justify-between gap-2.5">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-md shadow-emerald-500/25">
                                <Ticket className="h-4.5 w-4.5" />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-mono text-xs font-black tracking-wider text-emerald-950 dark:text-emerald-200 notranslate uppercase" translate="no">
                                    {appliedCoupon.code}
                                  </span>
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/50 px-2 py-0.5 text-[9px] font-black text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">
                                    <CheckCircle2 className="h-2.5 w-2.5" />
                                    Applied
                                  </span>
                                </div>
                                <p className="mt-0.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 notranslate" translate="no">
                                  -${promoDiscount.toFixed(2)} {t('cart.savings')}
                                  {appliedCoupon.maxDiscount && appliedCoupon.maxDiscount > 0 && (
                                    <span className="ml-1 opacity-75 font-normal">
                                      (Max ${appliedCoupon.maxDiscount})
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => dispatch(removePromoCode())}
                              className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 dark:text-gray-500 dark:hover:bg-red-950/30 dark:hover:text-red-400 transition-colors"
                              title="Remove coupon"
                              aria-label="Remove promo code"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Better Upsell Banner */}
                          {betterUpsellCoupon && (
                            <div className="mt-2.5 pt-2 border-t border-dashed border-emerald-300/80 dark:border-emerald-500/30 flex items-center gap-1.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
                              <Flame className="h-3.5 w-3.5 text-amber-500 shrink-0 animate-pulse" />
                              <span className="leading-tight">
                                Add ${(Number(betterUpsellCoupon.minPurchaseAmount || 0) - subtotal).toFixed(2)} more to get {betterUpsellCoupon.type === 'percentage' ? `${betterUpsellCoupon.value}%` : `$${Number(betterUpsellCoupon.value).toFixed(2)}`} OFF with code <strong className="font-mono underline notranslate">{betterUpsellCoupon.code}</strong>!
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-gray-200/80 bg-white/70 dark:border-white/10 dark:bg-white/[0.03] p-2.5 sm:p-3 shadow-xs">
                          {/* Input field */}
                          <div className="relative flex items-center gap-2 rounded-xl bg-white dark:bg-gray-900/90 border border-gray-200/90 dark:border-white/10 px-2.5 py-1 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 shadow-inner transition-all">
                            <Ticket className="h-4 w-4 text-primary-500 shrink-0" />
                            <input
                              type="text"
                              value={promoInput}
                              onChange={(e) => setPromoInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleApplyPromo();
                              }}
                              placeholder={t('cart.enter_promo')}
                              className="h-8 min-w-0 flex-1 bg-transparent font-mono text-xs font-bold uppercase tracking-wider text-gray-900 placeholder:normal-case placeholder:font-sans placeholder:font-medium placeholder-gray-400 focus:outline-none dark:text-white dark:placeholder-gray-500"
                              disabled={couponsLoading}
                            />
                            {promoInput && (
                              <button
                                type="button"
                                onClick={() => setPromoInput('')}
                                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-md transition-colors"
                                aria-label="Clear promo input"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={handleApplyPromo}
                              disabled={!promoInput.trim() || couponsLoading}
                              className="flex h-7 shrink-0 items-center justify-center gap-1 rounded-lg bg-primary-600 px-3 text-[11px] font-black text-white shadow-xs transition-all hover:bg-primary-700 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-white/10 dark:disabled:text-gray-500"
                            >
                              {couponsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <span>{t('cart.apply')}</span>}
                            </button>
                          </div>

                          {/* Toggle available offers */}
                          {activeCouponsList.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setShowOffers((prev) => !prev)}
                              className="mt-2 flex w-full items-center justify-between rounded-xl border border-dashed border-primary-300/80 bg-primary-50/60 px-2.5 py-1.5 text-left transition-all hover:border-primary-400 hover:bg-primary-50 dark:border-primary-500/30 dark:bg-primary-500/[0.07] dark:hover:bg-primary-500/[0.12] group"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-primary-600 text-white shadow-xs">
                                  <Percent className="h-3 w-3" />
                                </span>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="text-[11px] font-black text-primary-950 dark:text-primary-100">
                                    {activeCouponsList.length} {t('cart.available_offers_hint', 'Offers Available')}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0 text-primary-600 dark:text-primary-400">
                                <span className="text-[10px] font-bold group-hover:underline">
                                  {showOffers ? t('common.hide', 'Hide') : t('common.view_all', 'View All')}
                                </span>
                                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${showOffers ? 'rotate-180' : ''}`} />
                              </div>
                            </button>
                          )}

                          {/* Accordion for Available Offers */}
                          <AnimatePresence>
                            {showOffers && activeCouponsList.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden mt-2 pt-2 border-t border-dashed border-gray-200/80 dark:border-white/10"
                              >
                                <div className="flex justify-between items-center mb-1.5 px-0.5">
                                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                                    {t('cart.available_offers', 'Available Offers')}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setShowOffers(false)}
                                    className="p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded transition-colors"
                                    aria-label="Close offers"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>

                                <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
                                  {activeCouponsList.map((coupon) => {
                                    const minPurchase = Number(coupon.minPurchaseAmount || 0);
                                    const eligibility = checkCouponEligibility(coupon);
                                    const isEligible = eligibility.isEligible;
                                    const discountValueText = coupon.type === "percentage"
                                      ? (coupon.value + "%")
                                      : (typeof coupon.value === "number" ? "$" + coupon.value.toFixed(2) : String(coupon.value));

                                    return (
                                      <div
                                        key={coupon.code || coupon.id}
                                        className={`relative flex items-stretch rounded-xl border shrink-0 transition-all ${
                                          isEligible
                                            ? "border-emerald-300/90 bg-gradient-to-r from-emerald-50/70 via-white to-emerald-50/30 hover:border-emerald-400 dark:border-emerald-500/30 dark:from-emerald-950/30 dark:via-gray-900 dark:to-emerald-950/15 shadow-xs"
                                            : "border-gray-200/90 bg-gray-100/70 dark:border-white/10 dark:bg-white/[0.03] opacity-60 grayscale-[0.7]"
                                        }`}
                                      >
                                        {/* Left coupon stub with perforated border */}
                                        <div className={`w-[74px] sm:w-[82px] shrink-0 flex flex-col items-center justify-center py-2 px-1 border-r border-dashed ${
                                          isEligible
                                            ? "border-emerald-200 dark:border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                                            : "border-gray-200 dark:border-white/10 bg-gray-200/40 dark:bg-white/[0.04] text-gray-400 dark:text-gray-500"
                                        }`}>
                                          <span className="text-sm sm:text-base font-black leading-none notranslate" translate="no">
                                            {discountValueText}
                                          </span>
                                          <span className="text-[8px] font-black uppercase tracking-wider mt-0.5">
                                            OFF
                                          </span>
                                          {coupon.isUserAssigned && (
                                            <span className="mt-1 inline-block rounded bg-blue-100 dark:bg-blue-900/40 px-1 py-0.2 text-[8px] font-black text-blue-700 dark:text-blue-300 uppercase">
                                              VIP
                                            </span>
                                          )}
                                          {coupon.firstOrderOnly && (
                                            <span className="mt-1 inline-block rounded bg-amber-100 dark:bg-amber-900/40 px-1 py-0.2 text-[8px] font-black text-amber-700 dark:text-amber-300 uppercase">
                                              1st Order
                                            </span>
                                          )}
                                        </div>

                                        {/* Middle & Right: Details & Action */}
                                        <div className="flex-1 min-w-0 p-2 sm:p-2.5 flex flex-col justify-between gap-1">
                                          <div>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span className="font-mono text-xs font-black text-gray-950 dark:text-white notranslate tracking-wide" translate="no">
                                                {coupon.code || coupon.id}
                                              </span>
                                              {coupon.maxDiscount && coupon.maxDiscount > 0 && (
                                                <span className="text-[9px] font-semibold text-gray-400 dark:text-gray-500">
                                                  {"(Max $" + coupon.maxDiscount + ")"}
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                              {coupon.description || (minPurchase > 0 ? t("coupon.min_purchase", { amount: "$" + minPurchase.toFixed(2) }) : t("cart.available_offers"))}
                                            </p>
                                          </div>

                                          <div className="flex items-center justify-between gap-1.5 pt-0.5">
                                            {isEligible ? (
                                              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 shrink-0">
                                                <CheckCircle2 className="h-3 w-3 shrink-0" />
                                                {t("coupon.tap_to_apply", "Tap to apply")}
                                              </span>
                                            ) : (
                                              <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1 min-w-0 truncate">
                                                <Lock className="h-3 w-3 shrink-0 text-amber-500" />
                                                <span className="truncate">{eligibility.reason}</span>
                                              </span>
                                            )}

                                            {isEligible ? (
                                              <button
                                                type="button"
                                                onClick={() => handleDirectApply(coupon)}
                                                className="h-6 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black shadow-xs transition-all active:scale-95 flex items-center gap-1 shrink-0"
                                              >
                                                <span>{t("cart.apply")}</span>
                                                <ArrowRight className="h-2.5 w-2.5" />
                                              </button>
                                            ) : (
                                              <span className="h-5 px-2 rounded-md bg-gray-200/80 dark:bg-white/10 text-gray-500 dark:text-gray-400 text-[9px] font-bold flex items-center shrink-0">
                                                Locked
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}

                      {/* Messages */}
                      <AnimatePresence>
                        {couponError && (
                          <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-xs text-red-500 px-2 pb-1 pt-1">
                            {couponError}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                </div>
              )}
            </div>

            
{/* Footer with Totals and Checkout */}
            {cartItems.length > 0 && (
              <div className="border-t border-gray-200/80 bg-white/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-white/10 dark:bg-[#0b1220]/95 backdrop-blur-sm sm:p-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-8px_20px_rgba(0,0,0,0.06)]">
                {/* Price Breakdown */}
                <div className="mb-2.5 rounded-xl border border-gray-200/80 bg-gray-50/80 p-2.5 text-xs dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-gray-500 dark:text-gray-400">
                      <span>{t('cart.subtotal')}</span>
                      <span className="font-bold text-gray-800 dark:text-gray-200 notranslate" translate="no">${subtotal.toFixed(2)}</span>
                    </div>

                    {pointsDiscount > 0 && (
                      <div className="flex items-center justify-between font-semibold text-emerald-600 dark:text-emerald-400">
                        <span>{t('cart.points_discount')}</span>
                        <span className="notranslate" translate="no">-${pointsDiscount.toFixed(2)}</span>
                      </div>
                    )}

                    {promoDiscount > 0 && (
                      <div className="flex items-center justify-between font-semibold text-primary-600 dark:text-primary-400">
                        <span>{t('cart.promo_discount')}</span>
                        <span className="notranslate" translate="no">-${promoDiscount.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="pt-1.5 border-t border-gray-200/80 dark:border-white/10 flex items-baseline justify-between">
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">{t('cart.total')}</span>
                      <span className="text-lg font-black text-gray-950 dark:text-white notranslate" translate="no">${finalTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Checkout Button */}
                <button
                  type="button"
                  onClick={handleCheckout}
                  onMouseEnter={() => prefetchLiveRates()}
                  onTouchStart={() => prefetchLiveRates()}
                  onPointerDown={() => prefetchLiveRates()}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-bold text-white shadow-md shadow-primary-600/20 transition-all hover:bg-primary-700 active:scale-[0.99]"
                >
                  <CreditCard className="h-4 w-4" />
                  <span>{t('cart.proceed_checkout')}</span>
                </button>

                {/* Continue Shopping */}
                <Link
                  to="/products"
                  onClick={() => dispatch(setCartOpen(false))}
                  className="mt-1.5 flex w-full items-center justify-center gap-1 rounded-lg py-1 text-[11px] font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  {t('cart.continue_shopping')}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </motion.div>
        </React.Fragment>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
      />
    </AnimatePresence >
  );
};

export default Cart;
